import { Button, Space, Switch, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";

import type { ColumnsType } from "antd/es/table";
import { ICoupon } from "@interfaces";
import { EditOutlined } from "@ant-design/icons";
import { getApiErrorMessage, useNotify, useUpdateCouponFamily } from "@utils";

import { memo, useEffect } from "react";
import { Link } from "react-router-dom";
import { PATH_COUPON_FAMILY } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

interface IProps {
  data: ICoupon[];
  callback: () => void;
}
export const CouponTable = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();
  const updateCouponFamily = useUpdateCouponFamily();

  useEffect(() => {
    dispatch(setLoading(updateCouponFamily.isPending));
  }, [dispatch, updateCouponFamily.isPending]);

  const dataSource: ICoupon[] = data;

  const columns: ColumnsType<ICoupon> = [
    {
      title: t("page.program_name"),
      dataIndex: "programName",
      key: "1",
      render: (text, record) => (
        <Link to={`${PATH_COUPON_FAMILY}/${record.couponFamilyId}`}>
          {record?.program?.programName}
        </Link>
      ),
      sorter: (a, b) => {
        if (a?.program?.programName && b?.program?.programName) {
          return a?.program?.programName.localeCompare(b?.program?.programName);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.service_name"),
      dataIndex: "serviceName",
      key: "2",
      render: (text, record) => <span>{record?.service?.serviceName}</span>,
      sorter: (a, b) => {
        if (a?.service?.serviceName && b?.service?.serviceName) {
          return a?.service?.serviceName.localeCompare(b?.service?.serviceName);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.coupon_family"),
      dataIndex: "familyName",
      key: "2",
      render: (text, record) => <span>{record?.familyName}</span>,
      sorter: (a, b) => {
        if (a?.familyName && b?.familyName) {
          return a?.familyName.localeCompare(b?.familyName);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.total_numbers_of_coupon"),
      dataIndex: "noOfCoupons",
      key: "3",
      render: (tag) => (
        <Tag color={"#00AA67"} key={tag}>
          {tag}
        </Tag>
      ),
    },
    {
      title: t("page.total_numbers_of_used_coupon"),
      dataIndex: "noOfUsedCoupons",
      key: "4",
    },
    {
      title: t("page.actions"),
      key: "5",
      render: (_, record) => (
        <Space size="middle">
          <Switch checked={record?.status} onChange={(value: boolean) => onChange(value, record)} />
          {/* <Space>
            <Link to={PATH_COUPON_LIST + `/${record.couponFamilyId}`}>
              <Button type="ghost" icon={<EditOutlined />} size={"small"} />
              <span>{t("page.edit")}</span>
            </Link>
          </Space> */}
        </Space>
      ),
    },
  ];

  const onChange = async (value: boolean, record: ICoupon) => {
    try {
      await updateCouponFamily.mutateAsync({
        payload: {
          familyName: record?.familyName,
          programName: record?.program?.programName,
          serviceName: record?.service?.serviceName,
          programId: record?.program?.programId,
          serviceId: record?.service?.serviceId,
          noOfCoupons: record?.noOfCoupons,
          status: value,
        } as ICoupon,
        id: record.couponFamilyId || "",
      });
      callback();
      success(t("message.update.success"));
    } catch (err) {
      error(getApiErrorMessage(err, t("message.update.fail")));
    }
  };

  return (
    <Table dataSource={dataSource} rowKey={"couponFamilyId"} columns={columns} pagination={false} />
  );
});
